import React, { useState } from "react";
import { Dropdown, Row, Col } from "react-bootstrap";
import { GripVertical, PencilSquare, PlusCircleFill, Trash } from "react-bootstrap-icons";
import { ADMIN_LANG } from "../../../../settings";
import { Column as ColumnType, GridRow, SlideElement } from "../../../../types/admin";
import { v4 as uuidv4 } from "uuid";
import {
    DropResult,
    DragDropContext,
    Droppable,
    Draggable,
} from "@hello-pangea/dnd";
import Confirm from "../../Confirm";

const rowOptions = [
    {
        id: "row-1",
        columns: [{ mobile: 12, desktop: 12 }]
    },
    {
        id: "row-2",
        columns: [
            { mobile: 12, desktop: 4 },
            { mobile: 12, desktop: 8 }
        ]
    },
    {
        id: "row-3",
        columns: [
            { mobile: 12, desktop: 6 },
            { mobile: 12, desktop: 6 }
        ]
    },
    {
        id: "row-4",
        columns: [
            { mobile: 12, desktop: 8 },
            { mobile: 12, desktop: 4 }
        ]
    },
    {
        id: "row-5",
        columns: [
            { mobile: 12, desktop: 4 },
            { mobile: 12, desktop: 4 },
            { mobile: 12, desktop: 4 }
        ]
    },
];

const elementOptions = [
    { id: "text", label: ADMIN_LANG.text },
    { id: "image", label: ADMIN_LANG.image },
    { id: "video", label: ADMIN_LANG.video },
    { id: "vimeo", label: ADMIN_LANG.vimeo || "Vimeo" },
];

type SlideEditorProps = {
    slideRows: GridRow[];
    languageTab: string;
    onChangeRows: (rows: GridRow[]) => void;
    onDeleteRow: (rowId: string) => void;
    onDeleteElement: (elementId: string) => void;
    onOpenElementModal: (payload: {
        element_id: string | null;
        rowId: string;
        columnId: string;
        type: string;
    }) => void;
};

const SlideEditor = ({
    slideRows,
    languageTab,
    onChangeRows,
    onDeleteRow,
    onDeleteElement,
    onOpenElementModal,
}: SlideEditorProps) => {
    const handleAddRow = (layout: typeof rowOptions[0]) => {
        const newRow: GridRow = {
            row_id: uuidv4(),
            key: uuidv4(),
            columns: layout.columns.map((col) => ({
                column_id: uuidv4(),
                key: uuidv4(),
                elements: [],
                ...col
            }))
        };
        onChangeRows([newRow, ...slideRows]);
    };

    const handleOnDragEnd = (result: DropResult) => {
        if (!result.destination) return;

        const { source, destination, type } = result;

        if (source.droppableId === destination.droppableId && source.index === destination.index) {
            return;
        }

        if (type === "row") {
            const reorderedRows = [...slideRows];
            const [movedRow] = reorderedRows.splice(source.index, 1);
            reorderedRows.splice(destination.index, 0, movedRow);
            onChangeRows(reorderedRows);
        } else if (type === "element") {
            const sourceRowIndex = slideRows.findIndex((row) =>
                row.columns.some((col) => col.key === source.droppableId)
            );
            const destRowIndex = slideRows.findIndex((row) =>
                row.columns.some((col) => col.key === destination.droppableId)
            );
            const sourceColumn = slideRows[sourceRowIndex].columns.find(
                (col) => col.key === source.droppableId
            )!;
            const destColumn = slideRows[destRowIndex].columns.find(
                (col) => col.key === destination.droppableId
            )!;

            if (source.droppableId === destination.droppableId) {
                const reorderedElements = [...sourceColumn.elements];
                const [movedElement] = reorderedElements.splice(source.index, 1);
                reorderedElements.splice(destination.index, 0, movedElement);
                const updatedRows = slideRows.map((row, rowIndex) => {
                    if (rowIndex === sourceRowIndex) {
                        return {
                            ...row,
                            columns: row.columns.map((col) =>
                                col.key === sourceColumn.key
                                    ? { ...col, elements: reorderedElements }
                                    : col
                            ),
                        };
                    }
                    return row;
                });
                onChangeRows(updatedRows);
            } else {
                const sourceElements = [...sourceColumn.elements];
                const destElements = [...destColumn.elements];

                const [movedElement] = sourceElements.splice(source.index, 1);
                destElements.splice(destination.index, 0, movedElement);

                const updatedRows = [...slideRows];

                if (updatedRows[sourceRowIndex]) {
                    updatedRows[sourceRowIndex] = {
                        ...updatedRows[sourceRowIndex],
                        columns: updatedRows[sourceRowIndex].columns.map((col) =>
                            col.key === sourceColumn.key
                                ? { ...col, elements: sourceElements }
                                : col
                        ),
                    };
                }

                if (updatedRows[destRowIndex]) {
                    updatedRows[destRowIndex] = {
                        ...updatedRows[destRowIndex],
                        columns: updatedRows[destRowIndex].columns.map((col) =>
                            col.key === destColumn.key
                                ? { ...col, elements: destElements }
                                : col
                        ),
                    };
                }

                onChangeRows(updatedRows);
            }
        }
    };

    const handleAddElement = (rowId: string, columnId: string, type: string) => {
        onOpenElementModal({ element_id: null, rowId, columnId, type });
    };

    return (
        <div>
            <div className="d-flex align-items-center pb-2 pt-3">
                <h4 className="mb-0">Lisää rivi</h4>
                <Dropdown className="icon-dropdown ms-2">
                    <Dropdown.Toggle variant="icon">
                        <PlusCircleFill size="1.8rem" title="Lisää rivi" />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        {rowOptions.map((option) => (
                            <Dropdown.Item
                                key={option.id}
                                onClick={() => handleAddRow(option)}
                                className="dropdown-row-options"
                            >
                                <Row className="dropdown-row">
                                    {option.columns.map((col, index) => (
                                        <Col
                                            key={index}
                                            xs={col.mobile}
                                            md={col.desktop}
                                            className="dropdown-col"
                                        >
                                            <div className="dropdown-placeholder"></div>
                                        </Col>
                                    ))}
                                </Row>
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
            </div>

            <DragDropContext onDragEnd={handleOnDragEnd}>
                <Droppable droppableId="slideRows" type="row">
                    {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps}>
                            {slideRows.map((row, index) => (
                                <DraggableRow
                                    key={row.key}
                                    row={row}
                                    index={index}
                                    onDeleteRow={onDeleteRow}
                                    onOpenElementModal={onOpenElementModal}
                                    languageTab={languageTab}
                                    onDeleteElement={onDeleteElement}
                                />
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </div>
    );
};

const DraggableRow = ({
    row,
    index,
    onDeleteRow,
    onOpenElementModal,
    languageTab,
    onDeleteElement,
}: {
    row: GridRow;
    index: number;
    onDeleteRow: (rowId: string) => void;
    onOpenElementModal: (payload: {
        element_id: string | null;
        rowId: string;
        columnId: string;
        type: string;
    }) => void;
    languageTab: string;
    onDeleteElement: (elementId: string) => void;
}) => {
    const [showConfirm, setShowConfirm] = useState(false);

    const handleDeleteRow = () => {
        setShowConfirm(true);
    };

    const handleConfirmDelete = (rowId?: string) => {
        if (rowId) {
            onDeleteRow(rowId);
        }
        setShowConfirm(false);
    };

    const handleCancelDelete = () => {
        setShowConfirm(false);
    };

    return (
        <Draggable draggableId={row.key} index={index}>
            {(provided) => (
                <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    className="draggable-row d-flex align-items-center"
                >
                    <Row className="slide-editor-row flex-grow-1">
                        {row.columns.map((col) => (
                            <Column
                                key={col.key}
                                row={row}
                                column={col}
                                onOpenElementModal={onOpenElementModal}
                                languageTab={languageTab}
                                onDeleteElement={onDeleteElement}
                            />
                        ))}
                    </Row>
                    <div
                        className="utility-icons d-flex justify-content-center align-items-center"
                        style={{ width: "10%" }}
                    >
                        <div {...provided.dragHandleProps}>
                            <GripVertical className="utility-icon drag-icon" />
                        </div>
                        <Trash className="utility-icon red" onClick={handleDeleteRow} />
                    </div>
                    <Confirm
                        show={showConfirm}
                        message={ADMIN_LANG.confirm_delete_row}
                        itemId={row.row_id}
                        onConfirm={handleConfirmDelete}
                        onCancel={handleCancelDelete}
                    />
                </div>
            )}
        </Draggable>
    );
};

const Column = ({
    row,
    column,
    onOpenElementModal,
    languageTab,
    onDeleteElement,
}: {
    row: GridRow;
    column: ColumnType;
    onOpenElementModal: (payload: {
        element_id: string | null;
        rowId: string;
        columnId: string;
        type: string;
    }) => void;
    languageTab: string;
    onDeleteElement: (elementId: string) => void;
}) => {
    const getDraggableStyle = (isDragging: boolean, draggableStyle: any) => ({
        ...draggableStyle,
        ...(isDragging && {
            width: '19rem',
            height: 'auto',
            opacity: 0.8
        })
    });

    const handleAddElement = (rowId: string, columnId: string, type: string) => {
        onOpenElementModal({ element_id: null, rowId, columnId, type });
    };

    return (
        <Col xs={column.mobile} md={column.desktop} className="slide-editor-column">
            <Droppable droppableId={column.key} type="element">
                {(provided, snapshot) => (
                    <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`column-content ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
                    >
                        {column.elements.map((element, index) => (
                            <Draggable key={element.key} draggableId={element.key} index={index}>
                                {(draggableProvided, draggableSnapshot) => (
                                    <div
                                        ref={draggableProvided.innerRef}
                                        {...draggableProvided.draggableProps}
                                        {...draggableProvided.dragHandleProps}
                                        style={getDraggableStyle(
                                            draggableSnapshot.isDragging,
                                            draggableProvided.draggableProps.style
                                        )}
                                        className={`draggable-element ${draggableSnapshot.isDragging ? 'is-dragging' : ''}`}
                                    >
                                        <Element
                                            element={element}
                                            row={row}
                                            column={column}
                                            languageTab={languageTab}
                                            onOpenElementModal={onOpenElementModal}
                                            onDeleteElement={onDeleteElement}
                                        />
                                    </div>
                                )}
                            </Draggable>
                        ))}
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
            <div className="d-flex align-items-center">
                <p className='mb-0'>{ADMIN_LANG.add_content}</p>
                <Dropdown className="icon-dropdown ms-2">
                    <Dropdown.Toggle variant="icon">
                        <PlusCircleFill size="1.8rem" title="Add" />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        {elementOptions.map((option) => (
                            <Dropdown.Item
                                key={option.id}
                                onClick={() => handleAddElement(row.row_id, column.column_id, option.id)}
                            >
                                {option.label}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
            </div>
        </Col>
    );
};

const Element = ({
    element,
    row,
    column,
    languageTab,
    onOpenElementModal,
    onDeleteElement,
}: {
    element: SlideElement;
    row: GridRow;
    column: ColumnType;
    languageTab: string;
    onOpenElementModal: (payload: {
        element_id: string | null;
        rowId: string;
        columnId: string;
        type: string;
    }) => void;
    onDeleteElement: (elementId: string) => void;
}) => {
    const { text, video, image, vimeo } =
        element.languages.find((lang) => lang.language_code === languageTab) || {};
    const [showConfirm, setShowConfirm] = useState(false);

    const handleEditElement = () => {
        onOpenElementModal({
            element_id: element.element_id,
            rowId: row.row_id,
            columnId: column.column_id,
            type: element.type
        });
    };

    const handleDeleteElement = () => {
        setShowConfirm(true);
    };

    const handleConfirmDelete = (itemId?: string) => {
        if (itemId) {
            onDeleteElement(itemId);
        }
        setShowConfirm(false);
    };

    const handleCancelDelete = () => {
        setShowConfirm(false);
    };

    let renderElement = null;
    let elementName = element.type;
    if (element.type === "text") {
        elementName = ADMIN_LANG.text;
        const textDisplay = text || "";
        renderElement = <div className='element-body' dangerouslySetInnerHTML={{ __html: textDisplay }}></div>;
    } else if (element.type === "image") {
        elementName = ADMIN_LANG.image;
        renderElement = <div className='element-body'>
            <img src={image?.url} className='img-fluid' alt='Slide element' />
        </div>;
    } else if (element.type === "video") {
        elementName = ADMIN_LANG.video;
        renderElement = <div className='element-body'>
            <video key={video?.url} controls className='img-fluid'>
                <source src={video?.url} />
            </video>
        </div>;
    } else if (element.type === "vimeo") {
        elementName = ADMIN_LANG.vimeo || "Vimeo";
        renderElement = <div className='element-body'>
            <div className="ratio ratio-16x9">
                <iframe
                    src={vimeo?.url}
                    frameBorder={0}
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                    allowFullScreen
                    referrerPolicy="strict-origin-when-cross-origin"
                    title="Vimeo embed"
                />
            </div>
        </div>;
    }

    return (
        <div className="element">
            <div className="element-header">
                <span className='text-capitalize'>
                    <GripVertical className="utility-icon drag-icon" />
                    {elementName}
                </span>
                <div>
                    <PencilSquare className="utility-icon" onClick={handleEditElement} />
                    <Trash onClick={handleDeleteElement} className="utility-icon red" />
                </div>
            </div>
            {renderElement}
            <Confirm
                show={showConfirm}
                message={ADMIN_LANG.confirm_delete_element}
                itemId={element.element_id}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
            />
        </div>
    );
};

export default SlideEditor;
